import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { contentApi, batchApi, apiErrorMessage } from '../services/api';
import { useOrg } from '../context/OrgContext';
import useEnrolledExams from '../hooks/useEnrolledExams';
import useExamHierarchy from '../hooks/useExamHierarchy';
import { Button, Card, PageSkeleton, ErrorState, EmptyState, PageHeader, Spinner } from '../components/ui';
import NotesViewer from '../components/NotesViewer';
import ContentDialog from '../components/dialogs/ContentDialog';
import { anchorLabel } from '../utils/contentAnchor';

const TYPE_LABELS = {
  notes_pdf: 'Notes PDF',
  prepgen_notes: 'Prepgen.ai notes',
  video_lesson: 'Video lesson'
};

export default function ContentPage() {
  const org = useOrg();
  const navigate = useNavigate();

  // Primary: topic notes. Institutes browse their enrolled exams by topic and
  // maintain their OWN markdown notes per topic (stored org-scoped in Storage).
  const { exams, loading: examsLoading, error: examsError } = useEnrolledExams();
  const [selectedExamId, setSelectedExamId] = useState('');
  const [activeTopic, setActiveTopic] = useState(null); // { section, topic } opened in NotesViewer

  useEffect(() => {
    if (exams.length && !exams.some((e) => e.examId === selectedExamId)) {
      setSelectedExamId(exams[0].examId);
    }
  }, [exams, selectedExamId]);

  const selectedExam = exams.find((e) => e.examId === selectedExamId) || null;
  const { sections, loading: topicsLoading, error: topicsError } = useExamHierarchy(selectedExamId);

  // Secondary: existing "other content" track (video lessons / external links),
  // left as-is. Notes are NOT managed through here anymore — they use NotesViewer above.
  const [content, setContent] = useState([]);
  const [batches, setBatches] = useState([]);
  const [otherLoading, setOtherLoading] = useState(true);
  const [otherError, setOtherError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadOther = useCallback(() => {
    setOtherLoading(true);
    Promise.all([contentApi.list(org.id), batchApi.list(org.id)])
      .then(([contentRes, batchesRes]) => {
        setContent(contentRes.data || []);
        setBatches(batchesRes.data || []);
        setOtherError(null);
      })
      .catch((err) => setOtherError(apiErrorMessage(err, 'Failed to load content')))
      .finally(() => setOtherLoading(false));
  }, [org.id]);

  useEffect(() => { loadOther(); }, [loadOther]);

  if (examsLoading) return <PageSkeleton />;
  if (examsError) return <ErrorState message={examsError} />;
  if (!exams.length) {
    return (
      <EmptyState
        title="No enrolled exams yet"
        action={<Button variant="secondary" onClick={() => navigate('/exam-library')}>Open exam library</Button>}
      >
        Notes are organized by exam topic. Enroll an exam first, then customize its notes for your students.
      </EmptyState>
    );
  }

  const batchNames = Object.fromEntries(batches.map((b) => [b.id, b.name]));
  // Only the exam on screen has its topic tree loaded, so items pinned to a
  // different exam read as "One topic" rather than blocking the table on a
  // fetch per exam.
  const sectionsByExam = selectedExamId ? { [selectedExamId]: sections } : {};

  return (
    <>
      <PageHeader
        eyebrow="Learning content"
        title="Notes & lessons"
        subtitle="Customize your institute's own notes per topic, and publish other learning content."
      />

      <Card className="bank-content">
        <div className="bank-content-head">
          <div>
            <h2>Topic notes</h2>
            <p className="muted">
              Maintain your institute's own version of the notes for each topic — seeded from Prepgen's
              managed notes or written from scratch. Students see your version when you've published one.
            </p>
          </div>
          <div>
            <select
              className="bank-exam-filter"
              aria-label="Choose exam"
              value={selectedExamId}
              onChange={(e) => setSelectedExamId(e.target.value)}
            >
              {exams.map((x) => <option key={x.examId} value={x.examId}>{x.title}</option>)}
            </select>
          </div>
        </div>

        {topicsLoading ? (
          <div className="page-loading"><Spinner /> Loading topics…</div>
        ) : topicsError ? (
          <ErrorState message={topicsError} />
        ) : !sections.length ? (
          <EmptyState title="No topics yet">This exam has no topics to attach notes to yet.</EmptyState>
        ) : (
          <div className="notes-topic-list">
            {sections.map((section) => (
              <div key={section.id} className="topic-nav-group">
                <span className="topic-nav-label">{section.title}</span>
                {(section.topics || []).map((topic) => (
                  <div key={topic.id} className="topic-row">
                    <span className="hierarchy-bullet" />
                    <div className="topic-row-main">
                      <span className="topic-row-title">{topic.title || topic.name}</span>
                    </div>
                    <Button variant="text" onClick={() => setActiveTopic({ section, topic })}>
                      View / edit notes →
                    </Button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="section-title" style={{ marginTop: 24 }}>
        <div>
          <p className="eyebrow">Other content</p>
          <h2>Video lessons &amp; links</h2>
          <p>Attach recorded classes or external resources, pinned to a topic or to the whole course.</p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>+ Add content</Button>
      </div>
      <Card className="table-card">
        {otherLoading ? (
          <div className="page-loading"><Spinner /> Loading content…</div>
        ) : otherError ? (
          <ErrorState message={otherError} />
        ) : (
          <table>
            <thead><tr><th>Content</th><th>Type</th><th>Where students see it</th><th>Audience</th><th>Published</th></tr></thead>
            <tbody>
              {content.length ? content.map((x) => (
                <tr key={x.id}>
                  <td><b>{x.title}</b></td>
                  <td>{TYPE_LABELS[x.type] || x.type}</td>
                  <td>{anchorLabel(x, sectionsByExam)}</td>
                  <td>{x.scope === 'all' ? 'All students' : (batchNames[x.scope] || x.scope)}</td>
                  <td>{x.publishedAt ? new Date(x.publishedAt).toLocaleDateString() : '—'}</td>
                </tr>
              )) : (
                <tr><td colSpan="5">
                  <EmptyState title="No other content yet" action={<Button variant="secondary" onClick={() => setDialogOpen(true)}>Add content</Button>}>
                    Video lessons and external links you publish will appear here.
                  </EmptyState>
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </Card>

      {activeTopic && selectedExam && (
        <NotesViewer
          orgId={org.id}
          exam={selectedExam}
          section={activeTopic.section}
          topic={activeTopic.topic}
          onClose={() => setActiveTopic(null)}
        />
      )}
      {dialogOpen && (
        <ContentDialog
          batches={batches}
          exams={exams}
          onClose={() => setDialogOpen(false)}
          onPublished={() => { setDialogOpen(false); loadOther(); }}
        />
      )}
    </>
  );
}
